// 自動生成されたMstSubscriptionPlanセレクターウィジェット
// このファイルは自動生成されます。手動で編集しないでください。

import React, { useState, useMemo } from 'react';
import { MasterDataClient } from '../MasterDataClient';
import type { MstSubscriptionPlan, MstSubscriptionPlanID } from '../MasterTypes';
import './MasterSelector.css';

// 検索カラム設定の型
export interface SearchColumnConfig<T> {
  key: keyof T;
  type: 'string' | 'number';
  /** 表示名（プレースホルダーに使用） */
  displayName?: string;
}

// デフォルトの検索対象カラム（後から追加・変更可能）
const DEFAULT_SEARCH_COLUMNS: SearchColumnConfig<MstSubscriptionPlan>[] = [
  { key: 'id', type: 'number', displayName: 'ID' },
  { key: 'label', type: 'string', displayName: 'ラベル' },
];

export interface MstSubscriptionPlanSelectorProps {
  value?: MstSubscriptionPlanID;
  onChange?: (value: MstSubscriptionPlanID | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  allowEmpty?: boolean;
  className?: string;
  filter?: (item: MstSubscriptionPlan) => boolean;
  /** 検索対象カラムの設定（指定しない場合はデフォルト: id, label） */
  searchColumns?: SearchColumnConfig<MstSubscriptionPlan>[];
  /** ドロップダウンアイテムのラベル表示をカスタマイズ */
  renderItemLabel?: (item: MstSubscriptionPlan) => React.ReactNode;
  /** 選択済みアイテムの詳細表示をカスタマイズ */
  renderSelectedInfo?: (item: MstSubscriptionPlan) => React.ReactNode;
}

export const MstSubscriptionPlanSelector: React.FC<MstSubscriptionPlanSelectorProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  allowEmpty = true,
  className = "",
  filter,
  searchColumns = DEFAULT_SEARCH_COLUMNS,
  renderItemLabel,
  renderSelectedInfo,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // 検索対象カラム名からプレースホルダーを生成
  const defaultPlaceholder = useMemo(() => {
    const names = searchColumns.map(col => col.displayName || String(col.key));
    return `${names.join('、')}で検索...`;
  }, [searchColumns]);

  const effectivePlaceholder = placeholder || defaultPlaceholder;

  // マスタデータを取得
  const allItems = useMemo(() => {
    try {
      let items = MasterDataClient.getMstSubscriptionPlanAll();
      if (filter) {
        items = items.filter(filter);
      }
      return items;
    } catch (error) {
      console.error('MstSubscriptionPlanデータの読み込みに失敗しました:', error);
      return [];
    }
  }, [filter]);

  // 検索フィルタリング
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) {
      return allItems;
    }
    const term = searchTerm.toLowerCase();
    return allItems.filter((item) => {
      for (const col of searchColumns) {
        const val = item[col.key];
        if (val === undefined || val === null) continue;
        if (col.type === 'number') {
          if (String(val).includes(term)) return true;
        } else {
          if (String(val).toLowerCase().includes(term)) return true;
        }
      }
      return false;
    });
  }, [allItems, searchTerm, searchColumns]);

  // 選択中のアイテム
  const selectedItem = useMemo(() => {
    if (value === undefined || value === 0) return null;
    return allItems.find((item) => item.id === value) || null;
  }, [allItems, value]);

  // アイテム選択時のハンドラー
  const handleSelect = (item: MstSubscriptionPlan) => {
    onChange?.(item.id as MstSubscriptionPlanID);
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  // 未選択を選択時のハンドラー
  const handleSelectEmpty = () => {
    onChange?.(0 as MstSubscriptionPlanID);
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  // デフォルトのアイテムラベル表示
  const defaultRenderItemLabel = (item: MstSubscriptionPlan) => item.label || `ID: ${item.id}`;

  // デフォルトの選択済み情報表示
  const defaultRenderSelectedInfo = (item: MstSubscriptionPlan) => (
    <>
      <span className="master-selector-dropdown__selected-id">
        ID: {item.id}
      </span>
      <span className="master-selector-dropdown__selected-label">
        {item.label}
      </span>
    </>
  );

  return (
    <div className={`master-selector-dropdown ${className}`}>
      <input
        type="text"
        className="master-selector-dropdown__input"
        placeholder={
          selectedItem
            ? `${selectedItem.id}: ${selectedItem.label || `ID: ${selectedItem.id}`}`
            : effectivePlaceholder
        }
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setIsDropdownOpen(true);
        }}
        onFocus={() => setIsDropdownOpen(true)}
        onBlur={() => {
          setTimeout(() => {
            setIsDropdownOpen(false);
            setSearchTerm("");
          }, 200);
        }}
        disabled={disabled}
      />
      {selectedItem && !isDropdownOpen && (
        <div className="master-selector-dropdown__selected-info">
          {renderSelectedInfo ? renderSelectedInfo(selectedItem) : defaultRenderSelectedInfo(selectedItem)}
        </div>
      )}
      {isDropdownOpen && !disabled && (
        <div className="master-selector-dropdown__list">
          {allowEmpty && (
            <div
              className="master-selector-dropdown__item"
              onClick={handleSelectEmpty}
              onMouseDown={(e) => e.preventDefault()}
            >
              <div className="master-selector-dropdown__item-main">
                <span className="master-selector-dropdown__item-id">0</span>
                <span className="master-selector-dropdown__item-label">未選択</span>
              </div>
            </div>
          )}
          {filteredItems.length === 0 ? (
            <div className="master-selector-dropdown__empty">
              該当するデータが見つかりません
            </div>
          ) : (
            filteredItems.slice(0, 50).map((item) => (
              <div
                key={item.id}
                className="master-selector-dropdown__item"
                onClick={() => handleSelect(item)}
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className="master-selector-dropdown__item-main">
                  <span className="master-selector-dropdown__item-id">
                    {item.id}
                  </span>
                  <span className="master-selector-dropdown__item-label">
                    {renderItemLabel ? renderItemLabel(item) : defaultRenderItemLabel(item)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MstSubscriptionPlanSelector;